import React, { useEffect, useState } from 'react';
import { X, Languages } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * Detects the browser's auto-translation feature (Google Translate / Safari
 * Translate) and shows a discreet, dismissible notice suggesting to disable it
 * — auto-translation rewrites the DOM and can break interactive React pages.
 */
const isPageTranslated = () => {
  try {
    const html = document.documentElement;
    if (html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl')) return true;
    if (document.querySelector('.goog-te-banner-frame, #goog-gt-tt, .skiptranslate')) return true;
    // Translators wrap text nodes in <font> tags; a modern React app uses none.
    if (document.getElementsByTagName('font').length > 0) return true;
  } catch (_) { /* ignore */ }
  return false;
};

export const TranslationNotice = () => {
  const { language } = useLanguage();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('translateNoticeDismissed') === '1') return;

    let observer;
    const timers = [];
    const stop = () => {
      timers.forEach(clearTimeout);
      if (observer) { observer.disconnect(); observer = null; }
    };

    const check = () => {
      if (isPageTranslated()) {
        setShow(true);
        stop();  // detected — no need to keep observing (avoids overhead)
      }
    };

    // Translation usually kicks in shortly after load — re-check a few times.
    check();
    timers.push(setTimeout(check, 800), setTimeout(check, 2500), setTimeout(check, 5000));

    try {
      observer = new MutationObserver(check);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (_) { /* ignore */ }

    // Stop observing after a while regardless, to avoid perpetual overhead.
    timers.push(setTimeout(stop, 9000));

    return stop;
  }, []);

  if (!show) return null;

  const dismiss = () => {
    sessionStorage.setItem('translateNoticeDismissed', '1');
    setShow(false);
  };

  return (
    <div
      className="notranslate fixed bottom-0 inset-x-0 z-[9999] bg-[#004E64] text-white px-4 py-3 shadow-2xl"
      translate="no"
      data-testid="translation-notice"
    >
      <div className="max-w-3xl mx-auto flex items-center gap-3">
        <Languages size={20} className="shrink-0 text-[#FFD166]" />
        <p className="text-sm leading-snug flex-1">
          {language === 'pt'
            ? 'Parece que a tradução automática do navegador está ativa. Para uma melhor experiência (e evitar erros), desative-a neste site.'
            : 'Your browser auto-translation seems to be on. For a better experience (and to avoid errors), please turn it off on this site.'}
        </p>
        <button
          onClick={dismiss}
          aria-label="Dispensar"
          data-testid="translation-notice-dismiss"
          className="shrink-0 rounded-full p-1 hover:bg-white/10 transition-colors"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default TranslationNotice;
